<template>
    <div id="today-view__content"
         data-component="TodayViewLinearLayout"
         @click.stop="clearPopover">
        <div class="mobile-only ma-1 flex--grow flex--align-self-start alert alert-info">
            <p>
                {{ l('TODAY_VIEW.LINEAR_VIEW_HIDDEN') }}
            </p>
            <p>
                <button class="btn btn-default"
                        @click="switchLayout">
                    {{ l('TODAY_VIEW.SWITCH_TO_LIST_VIEW') }}
                </button>
            </p>
        </div>
        <div id="today-view__table-container"
             ref="content-table"
             class="desktop-only"
             @scroll="onRightScroll">
            <PageLoadingSpinner v-if="isLoading"></PageLoadingSpinner>
            <template v-else-if="Object.keys(events).length === 0">
                <NoResults icon="fa-regular fa-calendar-xmark"
                           lang="TODAY_VIEW.NO_EVENTS_BEFORE_FILTERS"></NoResults>
            </template>
            <template v-else-if="filteredEventsWithSessions.length === 0">
                <NoResults icon="fa-regular fa-filter-slash"
                           lang="TODAY_VIEW.NO_EVENTS_AFTER_FILTERS"></NoResults>
            </template>
            <template v-else>
                <table id="today-view__table"
                       ref="linear-table"
                       class="linear-view-table">
                    <tr>
                        <td class="linear-view-table__header linear-view-table__corner linear-view-table__cell"></td>
                        <td v-for="hour in hours"
                            :key="hour"
                            colspan="2"
                            class="linear-view-table__header linear-view-table__cell"
                            v-html="hour"></td>
                    </tr>
                    <tr v-for="event in filteredEventsWithSessions"
                        :key="event.ID"
                        :style="{ minHeight: getEventRowHeight(event) }">
                        <td class="linear-view-table__cell">
                            <div class="linear-view-table__cell-event"
                                 :class="{'linear-view-table__cell-event--inactive': event.status === 'Inactive'}"
                                 :style="{ minHeight: getEventRowHeight(event) }">
                                <TodayViewSessionImage :event="event" />
                                <div class="linear-view-table__cell-event-info">
                                    {{ event.name }}
                                </div>
                            </div>
                        </td>
                        <td v-for="i in 48"
                            :key="i"
                            class="linear-view-table__cell linear-view-table__half-hour">
                            <TodayViewLinearTableCell :tracks="sessionTracksByEventAndCell[event.ID]?.[i] || []"
                                                      :max-tracks="trackCountByEvent[event.ID] || 1"
                                                      :cell="i" />
                        </td>
                    </tr>
                    <tr>
                        <td class="linear-view-table__footer linear-view-table__footer-first-col"></td>
                        <td class="linear-view-table__footer linear-view-table__footer-body-col"
                            colspan="48"></td>
                    </tr>
                </table>
            </template>
        </div>
    </div>
</template>

<script>
import NoResults from '@/vue/components/NoResults';
import PageLoadingSpinner from '@/vue/components/PageLoadingSpinner';
import TodayViewLinearTableCell from '@/epos/vue/pages/today/components/TodayViewLinearTableCell';
import TodayViewSessionImage from '@/epos/vue/pages/today/components/TodayViewSessionImage';
import {
    calculateSessionTracks,
    sortSessionsByFilteredEventIDs,
} from './functions/todayViewLinearLayoutFunctions';
import { mapState } from 'vuex';
import { TodayViewLayoutName } from '@/epos/vue/pages/today/TodayViewLayoutName';

export default {
    name: 'TodayViewLinearLayout',

    components: {
        NoResults,
        PageLoadingSpinner,
        TodayViewLinearTableCell,
        TodayViewSessionImage,
    },

    props: {
        sessions: {
            type: Array,
            default: null,
        },
        events: {
            type: Object,
            default: () => ({}),
        },
        sessionHours: {
            type: Object,
            default: () => ({}),
        },
        sessionAvailabilities: {
            type: Object,
            default: () => ({}),
        },
        sessionCount: {
            type: Number,
            default: 0,
        },
        sessionCountBeforeFilters: {
            type: Number,
            default: 0,
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        showHoursList: {
            type: Boolean,
            default: false,
        },
        currentHour: {
            type: Number,
            default: null,
        },
    },

    emits: [
        'layout-change',
    ],

    data() {
        return {
            filteredEvents: {},
            sortedSessions: {},
            sessionTracksByEventAndCell: {},
            trackCountByEvent: {},
        };
    },

    computed: {
        ...mapState('today', ['date', 'filters', 'filteredEventIDs']),

        /**
         * @returns {SessionEvent[]}
         */
        filteredEventsWithSessions() {
            return Object.values(this.filteredEvents)
                .filter((event) => this.sortedSessions.hasOwnProperty(event.ID));
        },

        hours() {
            return Array.from({ length: 24 }, (_, i) => {
                let hour;
                let suffix;

                if (i === 0) {
                    hour = 12;
                    suffix = 'AM';
                } else if (i < 12) {
                    hour = i;
                    suffix = 'AM';
                } else if (i === 12) {
                    hour = 12;
                    suffix = 'PM';
                } else {
                    hour = i - 12;
                    suffix = 'PM';
                }

                return `<b>${hour}</b> ${suffix}`;
            });
        },
    },

    watch: {
        '$store.state.today.filteredEventIDs': {
            handler() {
                this.setFilteredEvents();
            },
            immediate: true,
        },
        'sessions': {
            handler(newSessions) {
                if (newSessions) {
                    this.sortedSessions = sortSessionsByFilteredEventIDs(newSessions, this.filteredEventIDs);
                    const trackData = calculateSessionTracks(this.events, this.sessionAvailabilities, this.sortedSessions);
                    this.sessionTracksByEventAndCell = trackData.tracksByEventAndCell;
                    this.trackCountByEvent = trackData.trackCountByEvent;
                }
            },
            immediate: true,
        },
    },

    created() {
        // Reset any active popovers.
        this.$store.commit('today/setActiveLinearPopover', null);
    },

    methods: {
        clearPopover() {
            this.$store.commit('today/setActiveLinearPopover', null);
        },
        setFilteredEvents() {
            let filteredEvents = {};
            this.filteredEventIDs.forEach((eventID) => {
                if (this.events?.hasOwnProperty(eventID)) {
                    filteredEvents[eventID] = this.events[eventID];
                }
            });
            this.filteredEvents = filteredEvents;
        },

        getEventRowHeight(event) {
            const trackCount = this.trackCountByEvent[event.ID] || 1;
            const trackHeight = 40; // Height of each track
            const gap = 4; // Gap between tracks
            const padding = 16; // Vertical padding in cell (2 * @half-gutter-width)

            return `${trackCount * trackHeight + (trackCount - 1) * gap + padding}px`;
        },

        switchLayout() {
            this.$emit('layout-change', TodayViewLayoutName.LIST);
        },

        scrollToHour() {
            const d = new Date();

            let hours = 8;
            let minutes = 0;

            const isToday = (
                this.date.getFullYear() === d.getFullYear()
                && this.date.getMonth() === d.getMonth()
                && this.date.getDate() === d.getDate()
            );

            if (isToday) {
                hours = d.getHours();
                minutes = d.getMinutes();
            }

            let scrollAmount = hours * 2 * 60;
            if (minutes >= 30) {
                scrollAmount += 60;
            }

            scrollAmount -= 15;

            this.$refs['content-table'].scrollLeft = Math.max(scrollAmount, 0);
        },
    },
};
</script>

<style lang="less">
@import "../../../../less/variables";

#today-view__table-container {
    overflow-x: scroll;
    scroll-behavior: smooth;
    width: 100%;

    // A fake column for when the table is not full height.
    background: linear-gradient(
        to right,
        @dt-grey-neutral-100 0,
        @dt-grey-neutral-100 199px,
        @default-lighter-grey 199px,
        @default-lighter-grey 200px,
        @white 200px
    );
}

.linear-view-table {
    border-collapse: initial;
    width: 3080px;
    table-layout: fixed;

    tr:first-child {
        position: sticky;
        top: 0;
        z-index: 102;
    }

    td:first-child {
        position: sticky;
        left: 0;
        background-color: @white;
        padding: 0 @padding-large-horizontal 0 0;
        border-right: 1px solid @default-lighter-grey;
        border-bottom: 1px solid @default-lighter-grey;
        width: 200px;
        z-index: 101;
        vertical-align: middle;
    }

    .linear-view-table__corner {
        position: sticky;
        left: 0;
        top: 0;
        z-index: 103;
        background-color: @dt-grey-neutral-100;
    }

    .linear-view-table__cell {
        border-right: 1px solid @default-lighter-grey;
        border-bottom: 1px solid @default-lighter-grey;
        padding: @half-gutter-width 0;
        vertical-align: middle;

        &.linear-view-table__half-hour {
            width: 60px;
            background-color: @dt-grey-neutral-50;
            &:nth-child(even) {
                border-right: 1px dashed @default-lighter-grey;
            }
        }

        &.linear-view-table__header {
            background-color: @dt-grey-neutral-100;
            padding-left: @padding-large-horizontal;
        }
    }

    .linear-view-table__footer {
        &.linear-view-table__footer-first-col {
            background-color: @dt-grey-neutral-100;
            height: 220px;
        }
        border-bottom: 0!important;
    }

    .linear-view-table__cell-event {
        display: flex;
        align-items: center;
        border-left: 5px solid transparent;
        padding: @half-gutter-width;
        min-height: 56px;

        &--inactive {
            border-left: 5px solid @brand-warning;
        }
        &-info {
            display: flex;
            flex-direction: column;
            font-size: 13px;
            flex-shrink: 1;
            flex-grow: 1;
            row-gap: @half-gutter-width;
        }
        .today-view-session__image {
            display: flex;
            flex-shrink: 0;
            height: 35px;
            width: 35px;
            &__initial {
                height: 35px;
                width: 35px;
                font-size: 20px;
            }
        }
    }

    tr {
        height: auto;
        min-height: 56px;
    }
}

@media (min-width: @screen-lg-min) {
    #today-view__table-container {
        background: linear-gradient(
            to right,
            @dt-grey-neutral-100 0,
            @dt-grey-neutral-100 299px,
            @default-lighter-grey 299px,
            @default-lighter-grey 300px,
            @white 300px
        );
    }
    .linear-view-table {
        width: 3180px;

        td:first-child {
            width: 300px;
        }
    }
}
</style>
